import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Car, Droplet, DollarSign, Truck } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import VehicleParametersSection from './VehicleParametersSection';
import RouteParametersSection from './RouteParametersSection';
import EcoModesSection from './EcoModesSection';
import AdditionalCostsSection from './AdditionalCostsSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета расхода топлива
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setComparisonResults,
  comparisonResults,
  fuelTypes,
  consumptionUnits,
  distanceUnits,
  priceUnits,
  saveVehicle 
}) => {
  
  // Состояние для имени сохраняемого автомобиля
  const [saveVehicleName, setSaveVehicleName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Обработка вложенных полей (например, additionalCosts.maintenance)
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: fieldValue
        }
      }));
    } else {
      setFormData(prev => {
        const newState = { ...prev, [name]: fieldValue };

        // Обновляем цену топлива автоматически при изменении типа топлива
        if (name === 'fuelType') {
          const selectedFuelType = fuelTypes.find(type => type.id === fieldValue);
          if (selectedFuelType) {
            newState.fuelPrice = selectedFuelType.defaultPrice.toString();
            
            // Если тип топлива - электричество, меняем единицы измерения
            if (fieldValue === 'electricity') {
              newState.consumptionUnit = 'kwh_100km';
              newState.priceUnit = 'rub_kwh';
            } else if (prev.fuelType === 'electricity') {
              // Если изменяем с электричества на обычное топливо
              newState.consumptionUnit = 'l_100km';
              newState.priceUnit = 'rub_l';
            }
          }
        }

        // Сбрасываем связанные поля если функция отключена
        if (name === 'includeAdditionalCosts' && !checked) {
          newState.additionalCosts = {
            maintenance: '0',
            insurance: '0',
            tax: '0',
            parking: '0',
            toll: '0',
            other: '0'
          };
        }
        
        if (name === 'includeEcoInfo' && !checked) {
          newState.ecoMode = {
            tireInflation: false,
            airFilter: false,
            airConditioner: false,
            cargoWeight: false,
            economicDriving: false
          };
        }

        if (name === 'includeRouteInfo' && !checked) {
          newState.routeInfo = {
            startCity: '',
            endCity: '',
            highwayPercentage: '70',
            cityPercentage: '30'
          };
        }

        return newState;
      });
    }

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [fuelTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      distance, fuelConsumption, fuelPrice, fuelType, calculationType,
      distanceUnit, consumptionUnit, priceUnit,
      includeAdditionalCosts, additionalCosts,
      vehicleInfo, includeEcoInfo, includeRouteInfo, routeInfo
    } = formData;

    // Парсинг числовых значений
    const numDistance = parseFloatOrDefault(distance, -1);
    const numFuelConsumption = parseFloatOrDefault(fuelConsumption, -1);
    const numFuelPrice = parseFloatOrDefault(fuelPrice, -1);
    
    // Валидация базовых полей в зависимости от типа расчета
    if (calculationType === 'consumption' || calculationType === 'cost') {
      if (numDistance <= 0) newErrors.distance = 'Расстояние должно быть положительным.';
    }

    if (calculationType === 'consumption' || calculationType === 'distance') {
      if (numFuelPrice <= 0) newErrors.fuelPrice = 'Цена топлива должна быть положительной.';
    }

    if (calculationType === 'distance' || calculationType === 'cost') {
      if (numFuelConsumption <= 0) newErrors.fuelConsumption = 'Расход топлива должен быть положительным.';
    }

    // Проверяем, что пользователь выбрал тип топлива
    if (!fuelType) newErrors.fuelType = 'Выберите тип топлива.';

    // Проверка единиц измерения
    if (!distanceUnit) newErrors.distanceUnit = 'Выберите единицу измерения расстояния.';
    if (!consumptionUnit) newErrors.consumptionUnit = 'Выберите единицу измерения расхода.';
    if (!priceUnit) newErrors.priceUnit = 'Выберите единицу измерения цены.';

    // Валидация дополнительных расходов
    if (includeAdditionalCosts) {
      Object.entries(additionalCosts).forEach(([key, value]) => {
        const numValue = parseFloatOrDefault(value, -1);
        if (numValue < 0) {
          newErrors[`additionalCosts.${key}`] = 'Значение не может быть отрицательным.';
        }
      });
    }

    // Проверка данных об автомобиле
    if (vehicleInfo.engineVolume) {
      const numEngineVolume = parseFloatOrDefault(vehicleInfo.engineVolume, -1);
      if (numEngineVolume <= 0 || numEngineVolume > 10) {
        newErrors['vehicleInfo.engineVolume'] = 'Объем двигателя должен быть от 0.1 до 10 л.';
      }
    }

    if (vehicleInfo.fuelTankCapacity) {
      const numFuelTankCapacity = parseFloatOrDefault(vehicleInfo.fuelTankCapacity, -1);
      if (numFuelTankCapacity <= 0 || numFuelTankCapacity > 1000) {
        newErrors['vehicleInfo.fuelTankCapacity'] = 'Объем бака должен быть от 1 до 1000 л.';
      }
    }

    if (vehicleInfo.averageSpeed) {
      const numAverageSpeed = parseFloatOrDefault(vehicleInfo.averageSpeed, -1);
      if (numAverageSpeed <= 0 || numAverageSpeed > 200) {
        newErrors['vehicleInfo.averageSpeed'] = 'Средняя скорость должна быть от 1 до 200 км/ч.';
      }
    }

    // Проверка информации о маршруте
    if (includeRouteInfo) {
      if (!routeInfo.startCity) newErrors['routeInfo.startCity'] = 'Укажите город отправления.';
      if (!routeInfo.endCity) newErrors['routeInfo.endCity'] = 'Укажите город назначения.';
      
      const highwayPercentage = parseFloatOrDefault(routeInfo.highwayPercentage, -1);
      const cityPercentage = parseFloatOrDefault(routeInfo.cityPercentage, -1);
      
      if (highwayPercentage < 0 || highwayPercentage > 100) {
        newErrors['routeInfo.highwayPercentage'] = 'Процент должен быть от 0 до 100.';
      }
      
      if (cityPercentage < 0 || cityPercentage > 100) {
        newErrors['routeInfo.cityPercentage'] = 'Процент должен быть от 0 до 100.';
      }
      
      if (highwayPercentage + cityPercentage !== 100 && 
          !newErrors['routeInfo.highwayPercentage'] && 
          !newErrors['routeInfo.cityPercentage']) {
        newErrors['routeInfo.highwayPercentage'] = 'Сумма процентов должна быть равна 100%.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Конвертация между единицами измерения
  const convertConsumption = (value, fromUnit, toUnit) => {
    // Базовая единица для конвертации - л/100км
    let baseValue;
    
    // Преобразование к базовой единице
    switch (fromUnit) {
      case 'l_100km': 
        baseValue = value; 
        break;
      case 'km_l': 
        baseValue = 100 / value; 
        break;
      case 'mpg_us': 
        baseValue = 235.215 / value; 
        break;
      case 'mpg_uk': 
        baseValue = 282.481 / value; 
        break;
      case 'kwh_100km': 
        baseValue = value / 9.7;  // примерное соотношение кВт·ч к литрам
        break;
      default: 
        baseValue = value;
    }
    
    // Преобразование из базовой единицы в целевую
    switch (toUnit) {
      case 'l_100km': 
        return baseValue;
      case 'km_l': 
        return 100 / baseValue;
      case 'mpg_us': 
        return 235.215 / baseValue;
      case 'mpg_uk': 
        return 282.481 / baseValue;
      case 'kwh_100km': 
        return baseValue * 9.7;
      default: 
        return baseValue;
    }
  };

  // Конвертация между единицами измерения расстояния
  const convertDistance = (value, fromUnit, toUnit) => {
    const conversionFactors = {
      km: 1,
      miles: 0.621371
    };
    
    return value * conversionFactors[toUnit] / conversionFactors[fromUnit];
  };

  // Расчет экологического эффекта от эко-мер
  const calculateEcoSavings = (baseFuelConsumption, ecoModes) => {
    let savings = 0;
    
    if (ecoModes.tireInflation) savings += 0.03; // Экономия до 3%
    if (ecoModes.airFilter) savings += 0.05; // Экономия до 5%
    if (ecoModes.airConditioner) savings += 0.1; // Экономия до 10%
    if (ecoModes.cargoWeight) savings += 0.02; // Экономия до 2%
    if (ecoModes.economicDriving) savings += 0.15; // Экономия до 15%
    
    // Ограничиваем максимальную экономию до 30%
    savings = Math.min(savings, 0.3);
    
    return baseFuelConsumption * savings;
  };

  // Функция расчета
  const calculateResults = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setComparisonResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          distance: distanceInput,
          fuelConsumption: fuelConsumptionInput,
          fuelPrice: fuelPriceInput,
          fuelType,
          calculationType,
          distanceUnit,
          consumptionUnit,
          priceUnit,
          includeAdditionalCosts,
          additionalCosts,
          vehicleInfo,
          includeEcoInfo,
          ecoMode,
          includeRouteInfo,
          routeInfo
        } = formData;

        // Парсинг входных данных
        const distanceValue = parseFloatOrDefault(distanceInput);
        const fuelConsumptionValue = parseFloatOrDefault(fuelConsumptionInput);
        const fuelPriceValue = parseFloatOrDefault(fuelPriceInput);
        
        // Получаем выбранный тип топлива
        const selectedFuelType = fuelTypes.find(type => type.id === fuelType) || fuelTypes[0];
        
        // Получаем факторы выбросов CO2
        const co2Factor = selectedFuelType.co2Factor;
        
        // Конвертация к стандартным единицам (л/100км, км)
        const standardFuelConsumption = convertConsumption(fuelConsumptionValue, consumptionUnit, 'l_100km');
        const standardDistance = convertDistance(distanceValue, distanceUnit, 'km');
        
        // Расчет сокращения расхода от эко-режимов
        const ecoSavings = includeEcoInfo 
            ? calculateEcoSavings(standardFuelConsumption, ecoMode)
            : 0;
        
        const effectiveFuelConsumption = standardFuelConsumption - ecoSavings;
        
        // Расчет необходимого количества топлива для поездки
        const fuelAmount = (standardDistance * effectiveFuelConsumption) / 100;
        
        // Расчет стоимости топлива для поездки
        const fuelCost = fuelAmount * fuelPriceValue;
        
        // Расчет выбросов CO2
        const co2Emissions = fuelAmount * co2Factor;
        
        // Расчет общей стоимости с дополнительными расходами
        let totalAdditionalCosts = 0;
        if (includeAdditionalCosts) {
          Object.values(additionalCosts).forEach(value => {
            totalAdditionalCosts += parseFloatOrDefault(value, 0);
          });
        }
        
        const totalTripCost = fuelCost + totalAdditionalCosts;
        
        // Расчет дополнительных показателей
        const fuelCostPer100km = (fuelPriceValue * effectiveFuelConsumption);
        const costPerKm = fuelCostPer100km / 100;
        
        // Если указан объем бака, рассчитываем запас хода
        const fuelTankCapacity = parseFloatOrDefault(vehicleInfo.fuelTankCapacity, 0);
        const rangeOnFullTank = fuelTankCapacity > 0 
            ? (fuelTankCapacity * 100) / effectiveFuelConsumption
            : 0;
        
        // Если указана средняя скорость, рассчитываем время в пути
        const averageSpeed = parseFloatOrDefault(vehicleInfo.averageSpeed, 0);
        const tripTimeHours = averageSpeed > 0 
            ? standardDistance / averageSpeed
            : 0;
        
        // --- Формируем результаты расчета ---
        const calculationResults = {
          // Основные результаты
          distance: standardDistance,
          distanceOriginal: distanceValue,
          distanceUnit,
          fuelConsumption: standardFuelConsumption,
          fuelConsumptionOriginal: fuelConsumptionValue,
          consumptionUnit,
          fuelConsumptionWithEco: effectiveFuelConsumption,
          ecoSavingsLiters: ecoSavings,
          ecoSavingsPercent: ecoSavings > 0 ? (ecoSavings / standardFuelConsumption) * 100 : 0,
          
          // Стоимость
          fuelPrice: fuelPriceValue,
          priceUnit,
          fuelAmount,
          fuelCost,
          additionalCosts: totalAdditionalCosts,
          totalTripCost,
          costPer100km: fuelCostPer100km,
          costPerKm,
          
          // Экологический эффект
          co2Emissions,
          co2EmissionsPerKm: co2Emissions / standardDistance,
          
          // Дополнительные показатели
          fuelType: selectedFuelType,
          rangeOnFullTank,
          tripTimeHours,
          tripTimeMinutes: tripTimeHours * 60,
          
          // Данные автомобиля
          vehicleMake: vehicleInfo.make,
          vehicleModel: vehicleInfo.model,
          vehicleYear: vehicleInfo.year,
          engineVolume: parseFloatOrDefault(vehicleInfo.engineVolume, 0),
          
          // Тип расчета
          calculationType
        };
        
        // Расчет сравнения с другими типами топлива
        const comparisons = fuelTypes.map(fuel => {
          // Пропускаем текущий тип топлива
          if (fuel.id === fuelType) {
            return {
              fuelType: fuel,
              // Копируем текущие результаты для выбранного типа топлива
              fuelConsumption: effectiveFuelConsumption,
              fuelAmount,
              fuelCost,
              costPer100km: fuelCostPer100km,
              co2Emissions,
              co2EmissionsPerKm: co2Emissions / standardDistance,
              isCurrent: true
            };
          }
          
          // Расчет эквивалентного расхода для других типов топлива
          let equivalentConsumption;
          
          if (fuel.id === 'electricity') {
            // Для электричества переводим из л/100км в кВт·ч/100км
            equivalentConsumption = effectiveFuelConsumption * 9.7;
          } else if (selectedFuelType.id === 'electricity') {
            // Если текущий - электричество, а сравниваем с топливом
            equivalentConsumption = effectiveFuelConsumption / 9.7;
          } else if (fuel.id === 'gas' || fuel.id === 'gas-methane') {
            // Для газа (пропан/метан) переводим с учетом энергетической ценности
            equivalentConsumption = effectiveFuelConsumption * 1.2;
          } else if (selectedFuelType.id === 'gas' || selectedFuelType.id === 'gas-methane') {
            // Если текущий - газ, а сравниваем с бензином или дизелем
            equivalentConsumption = effectiveFuelConsumption / 1.2;
          } else if (fuel.id === 'diesel' && selectedFuelType.id.includes('petrol')) {
            // Для дизеля (если текущий - бензин)
            equivalentConsumption = effectiveFuelConsumption * 0.85;
          } else if (selectedFuelType.id === 'diesel' && fuel.id.includes('petrol')) {
            // Для бензина (если текущий - дизель)
            equivalentConsumption = effectiveFuelConsumption / 0.85;
          } else {
            // По умолчанию используем тот же расход
            equivalentConsumption = effectiveFuelConsumption;
          }
          
          // Рассчитываем сравнительные показатели
          const comparisonFuelAmount = (standardDistance * equivalentConsumption) / 100;
          const comparisonFuelCost = comparisonFuelAmount * fuel.defaultPrice;
          const comparisonCostPer100km = (fuel.defaultPrice * equivalentConsumption);
          const comparisonCo2Emissions = comparisonFuelAmount * fuel.co2Factor;
          
          return {
            fuelType: fuel,
            fuelConsumption: equivalentConsumption,
            fuelAmount: comparisonFuelAmount,
            fuelCost: comparisonFuelCost,
            costPer100km: comparisonCostPer100km,
            co2Emissions: comparisonCo2Emissions,
            co2EmissionsPerKm: comparisonCo2Emissions / standardDistance,
            isCurrent: false,
            costDifference: fuelCost - comparisonFuelCost,
            costDifferencePercent: ((fuelCost - comparisonFuelCost) / fuelCost) * 100,
            co2Difference: co2Emissions - comparisonCo2Emissions,
            co2DifferencePercent: ((co2Emissions - comparisonCo2Emissions) / co2Emissions) * 100
          };
        });
        
        setResults(calculationResults);
        setComparisonResults(comparisons);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setComparisonResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setComparisonResults, setIsCalculating, fuelTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      distance: '100',
      fuelConsumption: '8.5',
      fuelPrice: '54.50',
      fuelType: 'petrol-95',
      calculationType: 'consumption',
      distanceUnit: 'km',
      consumptionUnit: 'l_100km',
      priceUnit: 'rub_l',
      includeAdditionalCosts: false,
      additionalCosts: {
        maintenance: '0',
        insurance: '0',
        tax: '0',
        parking: '0',
        toll: '0',
        other: '0'
      },
      vehicleInfo: {
        make: '',
        model: '',
        year: '',
        engineVolume: '',
        fuelTankCapacity: '',
        averageSpeed: '60'
      },
      includeEcoInfo: false,
      ecoMode: {
        tireInflation: false,
        airFilter: false,
        airConditioner: false,
        cargoWeight: false,
        economicDriving: false
      },
      includeRouteInfo: false,
      routeInfo: {
        startCity: '',
        endCity: '',
        highwayPercentage: '70',
        cityPercentage: '30'
      }
    });
    setResults(null);
    setComparisonResults(null);
    setErrors({});
    setSaveVehicleName('');
    setShowSaveDialog(false);
  };

  // Экспорт данных расчета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Параметр', 'Значение', 'Единица измерения'];
    
    const formatCsvNumber = (num) => {
      if (num === undefined || num === null) return '';
      return num.toFixed(2).replace('.', ',');
    };

    // Формируем строки данных
    const rows = [
      ['Расстояние', formatCsvNumber(results.distance), distanceUnits.find(u => u.id === results.distanceUnit)?.name || 'км'],
      ['Расход топлива', formatCsvNumber(results.fuelConsumption), consumptionUnits.find(u => u.id === results.consumptionUnit)?.name || 'л/100км'],
      ['Тип топлива', results.fuelType?.name || '', ''],
      ['Цена топлива', formatCsvNumber(results.fuelPrice), priceUnits.find(u => u.id === results.priceUnit)?.name || '₽/л'],
      ['Необходимое количество топлива', formatCsvNumber(results.fuelAmount), 'л'],
      ['Стоимость топлива', formatCsvNumber(results.fuelCost), '₽'],
      ['Дополнительные расходы', formatCsvNumber(results.additionalCosts), '₽'],
      ['Общая стоимость поездки', formatCsvNumber(results.totalTripCost), '₽'],
      ['Стоимость на 100 км', formatCsvNumber(results.costPer100km), '₽'],
      ['Стоимость на 1 км', formatCsvNumber(results.costPerKm), '₽/км'],
      ['Выбросы CO2', formatCsvNumber(results.co2Emissions), 'кг'],
      ['Выбросы CO2 на 1 км', formatCsvNumber(results.co2EmissionsPerKm), 'кг/км']
    ];
    
    // Если есть данные о запасе хода
    if (results.rangeOnFullTank > 0) {
      rows.push(['Запас хода на полном баке', formatCsvNumber(results.rangeOnFullTank), 'км']);
    }
    
    // Если есть данные о времени в пути
    if (results.tripTimeHours > 0) {
      rows.push(['Время в пути', formatCsvNumber(results.tripTimeHours), 'ч']);
      rows.push(['Время в пути (мин)', formatCsvNumber(results.tripTimeMinutes), 'мин']);
    }
    
    // Если включены эко-режимы
    if (results.ecoSavingsLiters > 0) {
      rows.push(['Экономия от эко-режима', formatCsvNumber(results.ecoSavingsLiters), 'л/100км']);
      rows.push(['Экономия от эко-режима (%)', formatCsvNumber(results.ecoSavingsPercent), '%']);
    }
    
    // Если есть данные о сравнении
    if (comparisonResults && comparisonResults.length > 0) {
      rows.push(['', '', '']);
      rows.push(['СРАВНЕНИЕ С ДРУГИМИ ВИДАМИ ТОПЛИВА', '', '']);
      
      comparisonResults.forEach(comp => {
        if (!comp.isCurrent) {
          rows.push([
            `${comp.fuelType.name} - расход`, 
            formatCsvNumber(comp.fuelConsumption), 
            'л/100км'
          ]);
          rows.push([
            `${comp.fuelType.name} - стоимость поездки`, 
            formatCsvNumber(comp.fuelCost), 
            '₽'
          ]);
          rows.push([
            `${comp.fuelType.name} - экономия`,
            formatCsvNumber(comp.costDifference), 
            '₽'
          ]);
        }
      });
    }

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + 
      headers.join(';') + '\n' + 
      rows.map(row => row.join(';')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_расхода_топлива.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения автомобиля
  const handleSaveVehicle = () => {
    if (showSaveDialog) {
      const savedResult = saveVehicle(saveVehicleName || null);
      if (savedResult) {
        alert(`Автомобиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveVehicleName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateResults();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Droplet className="w-6 h-6 text-blue-500" />
          Параметры расчета расхода топлива
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры расчета */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          fuelTypes={fuelTypes}
          consumptionUnits={consumptionUnits}
          distanceUnits={distanceUnits}
          priceUnits={priceUnits}
        />

        {/* Информация об автомобиле */}
        <VehicleParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры маршрута */}
        <RouteParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Эко-режимы */}
        <EcoModesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные расходы */}
        <AdditionalCostsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения автомобиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название автомобиля"
                value={saveVehicleName}
                onChange={(e) => setSaveVehicleName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveVehicle}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать расход топлива'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveVehicle}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить автомобиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;